import {
  arrayUnion,
  collection,
  doc,
  getDoc,
  writeBatch,
  type DocumentData,
} from "firebase/firestore";
import { db } from "@/firebaseConfig";
import type { Module, ActionContext } from "vuex";
import type { SongModel, SongVariantRef } from "@/models/interfaces";
import { songConverter } from "@/composables/useConverters";
import { deepStripUndefined } from "@/utils/firestoreClean";
import { detectSongLanguage } from "@/utils/songLanguage";

// A song's alternative versions. They are "variants" in code because
// `versions` already names the cache-busting counters and `arrangement` a
// chapter order; the interface calls them versions.
//
// A variant is a whole song document of its own in `songs`, so whatever opens
// a song by id (a schedule row, the editor, the API) opens a variant the same
// way. It points at its master with `variantOf` and is named by `variantName`;
// the master lists its variants in `variants`. A variant has no
// `title_lowercase`, the field every song list sorts on, and that alone keeps
// it out of them. Title and author are one for the whole family and are
// written to every member when one is saved; the rest is each variant's own.

export interface SongVariantsState {
  /** The master of the family last loaded; null before any was. */
  masterId: string | null;
  variants: SongVariantRef[];
}

export type SongFamily = SongVariantsState;

type RootState = unknown;

const songRef = (id: string) => doc(db, "songs", id);

/** A stored `variants` list, whatever shape an older document left it in. */
const storedVariants = (value: unknown): SongVariantRef[] =>
  (Array.isArray(value) ? value : [])
    .filter((entry) => typeof entry?.id === "string" && entry.id)
    .map((entry) => ({ id: entry.id, name: String(entry.name ?? "") }));

// Bumped on every load, so a slower answer for an earlier song is dropped.
let loadRequest = 0;

const state = (): SongVariantsState => ({
  masterId: null,
  variants: [],
});

const mutations = {
  SET_FAMILY(s: SongVariantsState, family: SongFamily) {
    s.masterId = family.masterId;
    s.variants = family.variants;
  },
};

const actions = {
  /**
   * Loads the family of a song, whichever member it is. Read from the master
   * every time: a song handed over by a list may be a cached row from before
   * a version was added.
   */
  async load(
    { commit }: ActionContext<SongVariantsState, RootState>,
    song: { id: string; variantOf?: string | null },
  ): Promise<SongFamily> {
    const request = ++loadRequest;
    const masterId = song.variantOf || song.id;
    const snap = await getDoc(songRef(masterId));
    const family = { masterId, variants: storedVariants(snap.data()?.variants) };
    if (request === loadRequest) commit("SET_FAMILY", family);
    return family;
  },

  /**
   * One member of a family as it is stored. Key, tempo and length are each
   * version's own, so a schedule row starting on a variant reads them here.
   */
  async fetch(
    _: ActionContext<SongVariantsState, RootState>,
    id: string,
  ): Promise<(SongModel & { id: string }) | null> {
    const snap = await getDoc(songRef(id).withConverter(songConverter));
    return snap.exists() ? { ...snap.data(), id: snap.id } : null;
  },

  /**
   * Adds a version of a song: a copy of `sourceId` as it is stored now, its
   * chapters, chords, arrangement and notes included, under the same master.
   * Returns the new version's id.
   */
  async create(
    { state, commit }: ActionContext<SongVariantsState, RootState>,
    payload: { sourceId: string; name: string },
  ): Promise<string> {
    const name = payload.name.trim();
    if (!name) throw new Error("A version needs a name");

    const sourceSnap = await getDoc(songRef(payload.sourceId));
    if (!sourceSnap.exists()) throw new Error("Unknown song");
    const copy: DocumentData = { ...sourceSnap.data() };
    const masterId: string = copy.variantOf || payload.sourceId;
    // Left behind: what makes a song a listed master, and the ids the editor
    // has saved into older documents.
    for (const field of ["title_lowercase", "variants", "id", "songId"]) {
      delete copy[field];
    }

    const ref = doc(collection(db, "songs"));
    const batch = writeBatch(db);
    batch.set(
      ref,
      deepStripUndefined({ ...copy, variantOf: masterId, variantName: name }),
    );
    batch.update(songRef(masterId), {
      variants: arrayUnion({ id: ref.id, name }),
    });
    await batch.commit();

    if (state.masterId === masterId) {
      commit("SET_FAMILY", {
        masterId,
        variants: [...state.variants, { id: ref.id, name }],
      });
    }
    return ref.id;
  },

  /**
   * Saves the song open in the editor. Title and author go to every member
   * of its family; the master's `title_lowercase` is brought in line with its
   * title, and a variant's name is kept in step with its master's list.
   * `listChanged` says the master now sorts elsewhere in the repertoire, so
   * the persisted page cursors want rebuilding.
   */
  async save(
    { commit }: ActionContext<SongVariantsState, RootState>,
    payload: { id: string; song: SongModel },
  ): Promise<{ listChanged: boolean }> {
    const song: DocumentData = { ...payload.song };
    // Saved under another song's id, an original would become a copy of one
    // of its versions, or a version a listed song of its own.
    if (song.songId && song.songId !== payload.id) {
      throw new Error("The song on screen is not the one being saved");
    }
    // The family list is only ever changed by the actions here, so an editor
    // holding an older copy of it can not undo one.
    delete song.variants;
    delete song.title_lowercase;
    // Each version's own, as a version may be a translation.
    song.language ??= detectSongLanguage(song as SongModel);

    const masterId: string = song.variantOf || payload.id;
    const isVariant = masterId !== payload.id;
    const masterSnap = await getDoc(songRef(masterId));
    let variants = storedVariants(masterSnap.data()?.variants);
    const shared = {
      title: String(song.title ?? ""),
      author: String(song.author ?? ""),
    };
    const titleLowercase = shared.title.toLowerCase();

    const batch = writeBatch(db);
    const ownRef = songRef(payload.id).withConverter(songConverter);
    if (isVariant) {
      const listed = variants.find((variant) => variant.id === payload.id);
      // A version can not be left nameless: there would be nothing to pick.
      song.variantName =
        String(song.variantName ?? "").trim() || listed?.name || "";
      variants = variants.map((variant) =>
        variant.id === payload.id
          ? { ...variant, name: song.variantName }
          : variant,
      );
      batch.set(ownRef, song, { merge: true });
      // A variant whose master is gone is still saved, as a song on its own.
      if (masterSnap.exists()) {
        batch.update(songRef(masterId), {
          ...shared,
          title_lowercase: titleLowercase,
          variants,
        });
      }
    } else {
      batch.set(
        ownRef,
        { ...song, title_lowercase: titleLowercase },
        { merge: true },
      );
    }
    for (const variant of variants) {
      if (variant.id !== payload.id) {
        batch.update(songRef(variant.id), shared);
      }
    }
    await batch.commit();

    commit("SET_FAMILY", { masterId, variants });
    return {
      listChanged: masterSnap.data()?.title_lowercase !== titleLowercase,
    };
  },

  /**
   * Deletes a song. A variant goes alone and leaves its master's list; a
   * master takes its variants with it, as they are versions of nothing
   * without it. Returns the master a deleted variant belonged to.
   */
  async remove(
    { state, commit }: ActionContext<SongVariantsState, RootState>,
    id: string,
  ): Promise<{ masterId: string | null }> {
    const snap = await getDoc(songRef(id));
    const masterId: string | null = snap.data()?.variantOf || null;
    const batch = writeBatch(db);
    batch.delete(songRef(id));

    if (!masterId) {
      for (const variant of storedVariants(snap.data()?.variants)) {
        batch.delete(songRef(variant.id));
      }
      await batch.commit();
      if (state.masterId === id) {
        commit("SET_FAMILY", { masterId: null, variants: [] });
      }
      return { masterId: null };
    }

    const masterSnap = await getDoc(songRef(masterId));
    const variants = storedVariants(masterSnap.data()?.variants).filter(
      (variant) => variant.id !== id,
    );
    if (masterSnap.exists()) batch.update(songRef(masterId), { variants });
    await batch.commit();
    if (state.masterId === masterId) {
      commit("SET_FAMILY", { masterId, variants });
    }
    return { masterId: masterSnap.exists() ? masterId : null };
  },
};

const getters = {
  family(state: SongVariantsState): SongFamily {
    return state;
  },
};

export const songVariants: Module<SongVariantsState, RootState> = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
export default songVariants;
